/*
*  signedin.js
*  Project: Kanoogi Platform - Profile Editing Page
*
*  Created by Chris Taylor on June 27, 2024
*  Copyright 2024-2026 Kanoogi Inc. | All Rights Reserved.
*
*/

'use strict';

let cachedjwt=null;

/* ---------------------------------------------------------------- */

// SetJWT() - Set the JWT token, which primarily means saving it to local storage and caching it in a variable

export function SetJWT(jwt)
	{
	if (jwt)														// If a token is passed in, save it
		{
		cachedjwt=jwt;
		localStorage.setItem('token',jwt);
		}
	else
		localStorage.removeItem('token');							// Otherwise remove it
	}
/* ---------------------------------------------------------------- */

// GetJWT() - Returns the current JWT token stored locally

export function GetJWT()
	{
	if (!cachedjwt)
		cachedjwt=localStorage.getItem('token')						// Fetch whatever is stored if not cached

	return(cachedjwt);
	}
/* ---------------------------------------------------------------- */

// How long to wait on /validate before calling it a failure.  Without this, a
// hung connect waits on the BROWSER's default timeout — minutes — and every page
// that awaits the answer sits there.  10s is generous for a slow mobile network
// and still well inside a user's patience.
const VALIDATE_TIMEOUT_MS=10000;

/* ---------------------------------------------------------------- */

// SignedIn() - Checks to see if the user is signed in or not
// Returns the /validate payload; {uid:0} means signed out.
//
// 🔴 THIS FUNCTION MUST ALWAYS SETTLE.  It used to be `new Promise(async(resolve)
// =>...)` with no reject and no try/catch, which is worse than it looks: when the
// body threw, the async executor's rejection went UNHANDLED and the returned
// promise stayed PENDING FOREVER.  It never rejected, so `await SignedIn()` hung
// and a try/catch wrapped around that await never fired.  Confirmed live in a
// browser: a /validate that answered with an HTML error page threw on .json()
// ("Unexpected token '<', \"<!DOCTYPE \"...") and the navbar stayed invisible
// indefinitely, because the code after the await simply never ran.
//
// Every page awaits this at boot (via optionpanel.ValidateLogin), so a failure
// has to degrade, not hang.  {uid:0} is the honest answer — it is byte-for-byte
// what /validate itself sends for a signed-out visitor, so callers take a path
// that already exists and the UI draws its signed-out state.

export async function SignedIn()
	{
	const controller=new AbortController();
	const timer=setTimeout(()=>controller.abort(),VALIDATE_TIMEOUT_MS);

	try
		{
		const response=await fetch('/validate',{method:'GET',headers:{'Content-Type':'application/json','Authorization':`Bearer ${GetJWT()}`},signal:controller.signal});

		// Check the status BEFORE parsing.  An edge 502 (or any proxy error) hands
		// back an HTML page, and .json() on that is the exact throw that used to
		// wedge the site.  Anything non-2xx is "not signed in" as far as the UI goes.
		if (!response.ok) return({uid:0});

		const signedin=await response.json();
		// (No status log — the resolved value flows into kanoogi.signedin
		// which is observable from any module that needs to read it; logging
		// to the page console leaks dev info to regular users.)

		return((signedin && typeof signedin==='object')?signedin:{uid:0});	// Return userid as an indicator of success or not
		}
	catch
		{
		return({uid:0});										// Offline, timed out, or a non-JSON body — degrade to signed out rather than hang
		}
	finally
		{
		clearTimeout(timer);
		}
	}
